import { useEffect, useRef } from 'react';

const TURNSTILE_SCRIPT_ID = 'cf-turnstile-api';
const TURNSTILE_SCRIPT_SRC =
  'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
const MAX_CDATA_LENGTH = 255;

function loadTurnstileScript() {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('Turnstile requires a browser'));
  }
  if (window.turnstile) {
    return Promise.resolve(window.turnstile);
  }

  const existing = document.getElementById(TURNSTILE_SCRIPT_ID);
  if (existing) {
    return new Promise((resolve, reject) => {
      if (window.turnstile) {
        resolve(window.turnstile);
        return;
      }
      existing.addEventListener('load', () => resolve(window.turnstile));
      existing.addEventListener('error', () =>
        reject(new Error('Failed to load Turnstile script'))
      );
    });
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.id = TURNSTILE_SCRIPT_ID;
    script.src = TURNSTILE_SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve(window.turnstile);
    script.onerror = () => reject(new Error('Failed to load Turnstile script'));
    document.head.appendChild(script);
  });
}

/**
 * Cloudflare Turnstile widget. Token is sent to esignet for siteverify;
 * the UI does not decide validity. action + cdata bind the token to the
 * endpoint and oauth session (transactionId).
 */
export default function TurnstileWidget({
  siteKey,
  language = 'en',
  onVerified,
  widgetKey,
  action,
  cdata,
}) {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const onVerifiedRef = useRef(onVerified);

  useEffect(() => {
    onVerifiedRef.current = onVerified;
  }, [onVerified]);

  useEffect(() => {
    let cancelled = false;

    const notify = (token) => {
      onVerifiedRef.current?.(token);
    };

    const renderWidget = async () => {
      if (!siteKey || !action || !cdata || !containerRef.current) {
        return;
      }
      if (String(cdata).length > MAX_CDATA_LENGTH) {
        console.error('Turnstile cdata exceeds 255 characters');
        notify(null);
        return;
      }

      try {
        const turnstile = await loadTurnstileScript();
        if (cancelled || !turnstile || !containerRef.current) {
          return;
        }

        if (widgetIdRef.current !== null && widgetIdRef.current !== undefined) {
          try {
            turnstile.remove(widgetIdRef.current);
          } catch (_) {
            /* ignore */
          }
          widgetIdRef.current = null;
        }

        containerRef.current.innerHTML = '';
        widgetIdRef.current = turnstile.render(containerRef.current, {
          sitekey: siteKey,
          language,
          theme: 'light',
          appearance: 'always',
          action,
          cdata: String(cdata),
          callback: (token) => {
            notify(token);
          },
          'expired-callback': () => {
            notify(null);
          },
          'error-callback': () => {
            notify(null);
          },
        });
      } catch (error) {
        console.error('Turnstile render failed', error);
        notify(null);
        if (containerRef.current) {
          containerRef.current.innerHTML =
            '<p class="text-sm text-red-600">Captcha failed to load. Check network access to challenges.cloudflare.com</p>';
        }
      }
    };

    renderWidget();

    return () => {
      cancelled = true;
      if (
        widgetIdRef.current !== null &&
        widgetIdRef.current !== undefined &&
        window.turnstile
      ) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch (_) {
          /* ignore */
        }
        widgetIdRef.current = null;
      }
    };
  }, [siteKey, language, widgetKey, action, cdata]);

  if (!siteKey) {
    return (
      <p className="text-sm text-red-600 text-center">
        Captcha site key is not configured.
      </p>
    );
  }

  if (!action || !cdata) {
    return (
      <p className="text-sm text-red-600 text-center">
        Captcha session binding is not available.
      </p>
    );
  }

  return (
    <div className="flex justify-center inji-turnstile-wrap">
      <div ref={containerRef} />
    </div>
  );
}
